import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, Users, Newspaper, Image, MessageSquare, 
  Settings, LogOut, Menu, X, ChevronRight, Bell,
  TrendingUp, Youtube
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLayout = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: getAuthHeaders()
        });
        setStats(response.data);
      } catch (error) {
        console.error('Erro ao buscar estatísticas:', error);
      }
    };
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Users, label: 'Artistas', path: '/admin/artistas', badge: stats?.artists },
    { icon: Newspaper, label: 'Notícias', path: '/admin/noticias', badge: stats?.news },
    { icon: Image, label: 'Galeria', path: '/admin/galeria', badge: stats?.gallery },
    { icon: MessageSquare, label: 'Contatos', path: '/admin/contatos', badge: stats?.pending_contacts, badgeColor: 'bg-red-500' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-[#0a0a0a] border-r border-white/5 flex flex-col transition-all duration-300 fixed h-full z-40`}>
        {/* Logo */}
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          {sidebarOpen ? (
            <img 
              src="https://customer-assets.emergentagent.com/job_event-puramania/artifacts/agcwure7_logo%20marca%202.png" 
              alt="Puramania" 
              className="h-10 w-auto"
            />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-r from-[#00F0FF] to-[#FF0099] rounded-lg flex items-center justify-center text-white font-bold">
              P
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-500 hover:text-white transition-colors"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-gradient-to-r from-[#00F0FF]/20 to-[#FF0099]/20 text-white border border-[#00F0FF]/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={20} className={isActive ? 'text-[#00F0FF]' : ''} />
                {sidebarOpen && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.badge > 0 && (
                      <span className={`${item.badgeColor || 'bg-[#00F0FF]'} text-white text-xs px-2 py-0.5 rounded-full`}>
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* YouTube Integration Notice */}
        {sidebarOpen && (
          <div className="p-4 m-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-500 text-sm font-medium mb-1">
              <Youtube size={16} />
              YouTube
            </div>
            <p className="text-xs text-gray-400">
              Integração preparada. Adicione credenciais quando pronto.
            </p>
          </div>
        )}

        {/* User */}
        <div className="p-4 border-t border-white/5">
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'justify-center'}`}>
            <div className="w-10 h-10 bg-gradient-to-r from-[#00F0FF] to-[#FF0099] rounded-full flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0) || 'A'}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-gray-500 text-xs truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className={`mt-4 flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors w-full ${!sidebarOpen && 'justify-center'}`}
          >
            <LogOut size={18} />
            {sidebarOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        {/* Top Bar */}
        <header className="bg-[#0a0a0a] border-b border-white/5 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Link to="/admin/dashboard" className="hover:text-white">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-white capitalize">
              {location.pathname.split('/').pop() || 'Dashboard'}
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white text-sm"
            >
              Ver Site →
            </a>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
